import React, { useState } from "react";
import {
    Card,
    CardContent,
    CardActions,
    CardHeader,
    Avatar,
    Grid,
    ExpansionPanel,
    ExpansionPanelSummary,
    Typography,
    ExpansionPanelDetails,
    FormControl,
    IconButton,
    Theme,
    makeStyles,
    Tooltip,
    OutlinedInput,
    Box
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { apiPost } from "../../services/api";
import ReplyIcon from "@material-ui/icons/Reply";
import PostAddIcon from "@material-ui/icons/PostAdd";
import Moment from "react-moment";
import { isEmptyOrSpaces } from "../../services/validators";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import BookmarkBorderIcon from "@material-ui/icons/BookmarkBorder";
import BookmarkIcon from "@material-ui/icons/Bookmark";
import { grey } from "@material-ui/core/colors";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import StarIcon from "@material-ui/icons/Star";
import { useAuth } from "../../services/authentication";
import RefreshIcon from "@material-ui/icons/Refresh";
import { useSnackbar } from "notistack";
import Skeleton from "@material-ui/lab/Skeleton";
import { Bookmark } from "@material-ui/icons";
export interface IThread {
    id: string;
    posts: IPost[];
}

export interface ICommenter {
    userId: string;
    displayName: string;
    flair: string;
}

export interface IPost {
    id: string;
    message: string;
    time: Date;
    commenter: ICommenter;
    properties: Dictionary<any>;
    favorited: boolean;
}

export interface PostReference {
    id: string;
    thread: string;
    topic: string;
}

interface CommentThreadProps {
    projectId: number;
    thread: IThread;
    topic: string;

    deletePost(post: PostReference): Promise<boolean>;
    toggleFavorite(post: PostReference): Promise<boolean>;
    applyBookmark(bookmark: string): Promise<void>;

    showFavoritedOnly: boolean;
}

const useCommentThreadSyles = makeStyles((theme: Theme) => ({
    root: {
        margin: theme.spacing(2.5)
    },
    expanded: {
        boxShadow: "none",
        "&$expanded": {
            margin: "0"
        },
        "&:before": {
            display: "none"
        }
    }
}));

const CommentThread: React.FC<CommentThreadProps> = props => {
    const EmptyReply = "";

    const [messages, setMessages] = useState<IPost[]>(props.thread.posts);

    const [filteredMessages, setFilteredMessages] = useState<IPost[]>([]);

    const [reply, setReply] = useState(EmptyReply);

    const classes = useCommentThreadSyles();
    const { enqueueSnackbar } = useSnackbar();
    React.useEffect(() => {
        if (props.showFavoritedOnly) {
            setFilteredMessages(messages.filter(m => m.favorited));
        } else {
            setFilteredMessages(messages);
        }
    }, [messages, props.showFavoritedOnly]);

    const post = async () => {
        if (reply) {
            try {
                const newThread = (await apiPost(
                    `comments/${props.projectId}/${props.topic}/${props.thread.id}`,
                    {
                        message: reply
                    }
                )) as IPost;

                setMessages(searches => [...searches, newThread]);

                setReply(EmptyReply);
            } catch {
                enqueueSnackbar(
                    `Unable to post comment. Please try again later.`,
                    { variant: "error" }
                );
            }
        }
    };

    const keyPress = async e => {
        if (e.key === "Enter" && e.ctrlKey) {
            await post();
        }
    };

    const deletePost = async (post: PostReference) => {
        const deleted = await props.deletePost(post);

        if (deleted) {
            setMessages(messages.filter(i => i.id !== post.id));
        }

        return deleted;
    };

    const toggleFavorite = async (post: PostReference) => {
        const favorited = await props.toggleFavorite(post);

        const msg = messages;

        msg.forEach(s => {
            if (s.id === post.id) {
                s.favorited = !s.favorited;
                return;
            }
        });

        setMessages(msg);

        return favorited;
    };

    const applyBookmark = async (bookmark: string) => {
        if (props.applyBookmark) {
            await props.applyBookmark(bookmark);
        }
    };

    const multipleReplys = filteredMessages.length > 2;

    return (
        <Card className={classes.root} elevation={2}>
            {multipleReplys ? (
                <React.Fragment>
                    <Comment
                        deletePost={deletePost}
                        threadId={props.thread.id}
                        topic={props.topic}
                        comment={filteredMessages[0]}
                        applyBookmark={applyBookmark}
                        toggleFavorite={toggleFavorite}
                        showBookmark={true}
                        variant="Default"
                    />
                    <ExpansionPanel
                        className={classes.expanded}
                        style={{ background: grey[100] }}
                    >
                        <ExpansionPanelSummary
                            style={{ background: grey[100], minHeight: 0 }}
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                        >
                            <Typography variant="caption">
                                View all {filteredMessages.length - 1} Replies
                            </Typography>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails
                            style={{ padding: "0px", background: grey[100] }}
                        >
                            <Grid container spacing={5}>
                                {filteredMessages.map((c, i) => {
                                    if (i !== 0) {
                                        return (
                                            <Grid item xs={12} key={i}>
                                                <Comment
                                                    variant="Reply"
                                                    deletePost={deletePost}
                                                    toggleFavorite={
                                                        toggleFavorite
                                                    }
                                                    threadId={props.thread.id}
                                                    topic={props.topic}
                                                    comment={c}
                                                    key={i}
                                                    applyBookmark={
                                                        applyBookmark
                                                    }
                                                />
                                            </Grid>
                                        );
                                    }
                                })}
                            </Grid>
                        </ExpansionPanelDetails>
                    </ExpansionPanel>
                </React.Fragment>
            ) : (
                <React.Fragment>
                    {filteredMessages.map((c, i) => {
                        return (
                            <Comment
                                deletePost={deletePost}
                                threadId={props.thread.id}
                                toggleFavorite={toggleFavorite}
                                topic={props.topic}
                                comment={c}
                                key={i}
                                showBookmark={i == 0}
                                applyBookmark={applyBookmark}
                                variant={i == 0 ? "Default" : "Reply"}
                            />
                        );
                    })}
                </React.Fragment>
            )}

            {!props.showFavoritedOnly && (
                <CardActions style={{ background: grey[100] }}>
                    <FormControl size="small" fullWidth>
                        <OutlinedInput
                            fullWidth
                            multiline={true}
                            placeholder="Enter your reply here."
                            value={reply}
                            onKeyDown={keyPress}
                            onChange={e => setReply(e.target.value)}
                            endAdornment={
                                <IconButton
                                    style={{ padding: 0 }}
                                    disabled={!reply}
                                    onClick={post}
                                >
                                    <ReplyIcon />
                                </IconButton>
                            }
                        />
                    </FormControl>
                </CardActions>
            )}
        </Card>
    );
};

export type CommentVariant = "Default" | "Reply";

interface CommentProps {
    comment: IPost;
    threadId: string;
    topic: string;

    toggleFavorite(post: PostReference): Promise<boolean>;
    deletePost(post: PostReference): Promise<boolean>;
    applyBookmark(bookmark: string): Promise<void>;

    showBookmark?: boolean;

    variant: CommentVariant;
}

const useStyles = makeStyles((theme: Theme) => ({
    DefaultHeader: {
        padding: theme.spacing(1, 1, 0, 2)
    },
    DefaultContent: {
        padding: theme.spacing(0, 1, 1, 12),
        "&:last-child": {
            paddingBottom: 0
        }
    },
    ReplyHeader: {
        padding: theme.spacing(1, 1, 0, 2),
        background: grey[100]
    },
    ReplyContent: {
        padding: theme.spacing(0, 1, 1, 12),
        background: grey[100],
        "&:last-child": {
            paddingBottom: 0
        }
    }
}));

const Comment: React.FC<CommentProps> = props => {
    const deleteComment = async () => {
        await props.deletePost({
            id: props.comment.id,
            thread: props.threadId,
            topic: props.topic
        });
    };

    const classes = useStyles();

    const user = useAuth();

    const canDeleteComment = () => {
        return (
            user.authState.user?.isReservoirPortalAdministrator ||
            user.authState.user?.id == props.comment.commenter.userId
        );
    };

    const applyBookmark = async () => {
        const bookmark = props.comment.properties["power-bi-bookmark"];

        if (bookmark && props.applyBookmark) {
            await props.applyBookmark(bookmark);
        }
    };

    const toggleFavorite = async () => {
        if (props.toggleFavorite) {
            const result = await props.toggleFavorite({
                id: props.comment.id,
                thread: props.threadId,
                topic: props.topic
            });

            setFavorited(result);
        }
    };

    const [favorited, setFavorited] = React.useState(props.comment.favorited);

    const hasBookmark = () => {
        if (props.comment) {
            if (props.comment.properties) {
                return props.comment.properties["power-bi-bookmark"];
            }
        }

        return false;
    };

    const getDisplayName = () => {
        if (props.comment.commenter.flair) {
            return `${props.comment.commenter.displayName} - ${props.comment.commenter.flair}`;
        }

        return props.comment.commenter.displayName;
    };

    return (
        <React.Fragment>
            <CardHeader
                className={
                    props.variant == "Default"
                        ? classes.DefaultHeader
                        : classes.ReplyHeader
                }
                style={{ textAlign: "left" }}
                avatar={
                    <Avatar
                        style={{ height: "24px", width: "24px" }}
                        aria-label="recipe"
                    ></Avatar>
                }
                title={getDisplayName()}
                subheader={<Moment fromNow date={props.comment.time} />}
                action={
                    <div style={{ padding: 5 }}>
                        <Tooltip
                            title="Set as favorite"
                            aria-label="set as favorite"
                        >
                            <IconButton
                                onClick={toggleFavorite}
                                aria-label="Favorite"
                                style={{ padding: 0 }}
                            >
                                {favorited ? (
                                    <StarIcon color="primary" />
                                ) : (
                                    <StarBorderIcon />
                                )}
                            </IconButton>
                        </Tooltip>

                        {props.showBookmark && (
                            <Tooltip
                                title="Apply bookmark"
                                aria-label="Apply bookmark"
                            >
                                <IconButton
                                    onClick={applyBookmark}
                                    aria-label="bookmark"
                                    disabled={!hasBookmark()}
                                    style={{ padding: 0 }}
                                >
                                    {hasBookmark() ? (
                                        <BookmarkIcon color="primary" />
                                    ) : (
                                        <BookmarkBorderIcon />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}

                        {canDeleteComment() && (
                            <Tooltip
                                title="Delete comment"
                                aria-label="Delete comment"
                            >
                                <IconButton
                                    onClick={deleteComment}
                                    aria-label="delete"
                                    style={{ padding: 0 }}
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                }
            />
            <CardContent
                className={
                    props.variant == "Default"
                        ? classes.DefaultContent
                        : classes.ReplyContent
                }
                style={{ textAlign: "left" }}
            >
                <Typography variant="body1">
                    <pre
                        style={{
                            fontFamily: "inherit",
                            fontSize: 14,
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word"
                        }}
                    >
                        {props.comment.message}
                    </pre>
                </Typography>
            </CardContent>
        </React.Fragment>
    );
};

export enum ThreadOrder {
    Newest = "newest_first",
    Oldest = "oldest_first"
}

interface NewCommentProps {
    create(message: string): Promise<void>;
    toggleFavorites(): Promise<void>;
    refreshComments(): Promise<void>;
    isLoading: boolean;
    bookmarkApplied: boolean;
    clearBookmark(): Promise<void>;
}

const NewComment: React.FC<NewCommentProps> = props => {
    const [message, setMessage] = useState("");

    const post = async () => {
        if (!isEmptyOrSpaces(message)) {
            if (props.create) {
                await props.create(message);
            }
            setMessage("");
        }
    };

    const keyPress = async e => {
        if (e.key === "Enter" && e.ctrlKey) {
            await post();
        }
    };

    const [a, setA] = React.useState(false);

    const toggleFavorites = async () => {
        if (props.toggleFavorites) {
            await props.toggleFavorites();
            setA(!a);
        }
    };

    const refreshComments = async () => {
        if (props.refreshComments) {
            await props.refreshComments();
        }
    };

    return (
        <Card elevation={3}>
            <CardHeader
                action={
                    <>
                        {props.bookmarkApplied && (
                            <Tooltip title="Clear bookmark.">
                                <IconButton
                                    style={{ padding: 0 }}
                                    onClick={props.clearBookmark}
                                    aria-label="Clear bookmark"
                                >
                                    <Bookmark color="error" />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Tooltip title="Refresh comments list.">
                            <IconButton
                                style={{ padding: 0 }}
                                onClick={refreshComments}
                                aria-label="Favorite"
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Show favorited comments.">
                            <IconButton
                                style={{ padding: 0 }}
                                onClick={toggleFavorites}
                                aria-label="Favorite"
                            >
                                {a ? (
                                    <StarIcon color="primary" />
                                ) : (
                                    <StarIcon color="disabled" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </>
                }
            ></CardHeader>
            <CardActions>
                <FormControl fullWidth variant="filled">
                    <OutlinedInput
                        disabled={props.isLoading || a}
                        fullWidth
                        placeholder="Enter your comments here."
                        multiline={true}
                        onKeyDown={keyPress}
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        endAdornment={
                            <IconButton
                                style={{ padding: 0 }}
                                disabled={isEmptyOrSpaces(message)}
                                onClick={post}
                            >
                                <PostAddIcon />
                            </IconButton>
                        }
                    />
                </FormControl>
            </CardActions>
        </Card>
    );
};

interface TopicProps {
    projectId: number;
    topic: string;
    onPostMessage?(msg: INewPost): Promise<void>;

    realtimeCommenting?: boolean;

    threads: IThread[];
    isLoading: boolean;

    deletePost(post: PostReference): Promise<boolean>;
    applyBookmark(bookmark: string): Promise<void>;
    toggleFavorite(post: PostReference): Promise<boolean>;
    refreshComments(): Promise<void>;
    clearBookmark(): Promise<void>;
}

export interface Dictionary<T> {
    [Key: string]: T;
}

export interface INewPost {
    message: string;
    properties: Dictionary<any>;
}

const Comments: React.FC<TopicProps> = props => {
    const create = async (message: string) => {
        const msg = {
            message: message.trim(),
            properties: {}
        };

        if (props.onPostMessage) {
            await props.onPostMessage(msg);
        }
    };

    const [bookmarkApplied, setBookmarkApplied] = React.useState(false);

    const applyBookmark = async (bookmark: string) => {
        if (props.applyBookmark) {
            await props.applyBookmark(bookmark);

            setBookmarkApplied(true);
        }
    };

    const refreshComments = async () => {
        if (props.refreshComments) {
            await props.refreshComments();
        }
    };

    const clearBookmark = async () => {
        if (props.clearBookmark) {
            await props.clearBookmark();
        }
        setBookmarkApplied(false);
    };

    const [showFavoritedOnly, setShowFavoritedOnly] = React.useState(false);

    const toggleFavorites = async () => {
        setShowFavoritedOnly(!showFavoritedOnly);
    };

    return (
        <section>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <NewComment
                        toggleFavorites={toggleFavorites}
                        refreshComments={refreshComments}
                        bookmarkApplied={bookmarkApplied}
                        clearBookmark={clearBookmark}
                        create={create}
                        isLoading={props.isLoading}
                    />
                </Grid>

                <div
                    style={{ height: "500px", overflow: "auto", width: "100%" }}
                >
                    {props.isLoading && (
                        <>
                            {[...Array(4)].map(() => (
                                <Grid item xs={12}>
                                    <Box display="flex" alignItems="center">
                                        <Box margin={1}>
                                            <Skeleton
                                                variant="circle"
                                                style={{
                                                    height: "24px",
                                                    width: "24px"
                                                }}
                                            ></Skeleton>
                                        </Box>
                                        <Box width="100%">
                                            <Skeleton
                                                width="100%"
                                                height={100}
                                            ></Skeleton>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </>
                    )}

                    {props.threads.map(thread => {
                        return (
                            <Grid item xs={12} key={thread.id}>
                                <CommentThread
                                    projectId={props.projectId}
                                    topic={props.topic}
                                    deletePost={props.deletePost}
                                    toggleFavorite={props.toggleFavorite}
                                    thread={thread}
                                    showFavoritedOnly={showFavoritedOnly}
                                    applyBookmark={applyBookmark}
                                />
                            </Grid>
                        );
                    })}
                </div>
            </Grid>
        </section>
    );
};

export default Comments;
